import { Injectable, OnDestroy } from '@angular/core';

/**
 * WebLockService
 *
 * A generic, reusable wrapper around the Web Locks API (`navigator.locks`).
 *
 * Purpose:
 *   Acquiring a Web Lock prevents Chrome (and other Chromium-based browsers)
 *   from freezing a background tab. Chrome freezes all JS execution — including
 *   Web Workers — in tabs that have been hidden and idle for ~5 minutes.
 *   Holding a Web Lock signals to the browser that the tab is doing important
 *   coordination work and should not be frozen.
 *
 * Design:
 *   - Supports multiple named locks simultaneously (e.g., different subsystems
 *     can each hold their own lock).
 *   - Each lock is held by keeping a Promise unresolved. Calling `release(name)`
 *     resolves the Promise, which releases the lock.
 *   - Falls back gracefully on browsers that don't support the API (~4%).
 *   - All locks are released automatically on `ngOnDestroy`.
 *
 * Browser compatibility:
 *   Chrome 69+, Edge 79+, Firefox 96+, Safari 15.4+, Opera 56+
 *
 * Usage:
 *   this.webLockService.acquire('my-feature-keepalive');
 *   // ... later ...
 *   this.webLockService.release('my-feature-keepalive');
 */
@Injectable({ providedIn: 'root' })
export class WebLockService implements OnDestroy {

  /** Whether the Web Locks API is available in this browser. */
  readonly isSupported: boolean = typeof navigator !== 'undefined' && 'locks' in navigator;

  /** Tracks all currently held or pending locks by name. */
  private readonly activeLocks = new Map<string, {
    resolve: (() => void) | null;
    abortController: AbortController;
  }>();

  /**
   * Acquire a named Web Lock. If the lock is already held, this is a no-op.
   *
   * @param lockName Unique name for the lock (scoped to the origin).
   * @returns true if the lock was requested, false if unsupported or already held.
   */
  acquire(lockName: string): boolean {
    if (!this.isSupported) return false;
    if (this.activeLocks.has(lockName)) return false; // Already held

    const abortController = new AbortController();
    const entry = { resolve: null as (() => void) | null, abortController };
    this.activeLocks.set(lockName, entry);

    navigator.locks.request(
      lockName,
      { signal: abortController.signal },
      () => new Promise<void>(resolve => {
        // Store resolve so release() can call it later.
        // The lock is held as long as this Promise remains unresolved.
        const current = this.activeLocks.get(lockName);
        if (current) {
          current.resolve = resolve;
        } else {
          // Lock was released before the callback fired — resolve immediately.
          resolve();
        }
      })
    ).catch(() => {
      // Expected when AbortController.abort() is called or the lock request
      // was superseded. Clean up the entry if it still exists.
      this.activeLocks.delete(lockName);
    });

    return true;
  }

  /**
   * Release a named Web Lock.
   *
   * @param lockName The lock name passed to `acquire()`.
   * @returns true if a lock was released, false if no such lock was held.
   */
  release(lockName: string): boolean {
    const entry = this.activeLocks.get(lockName);
    if (!entry) return false;

    // Resolve the Promise to release the lock cleanly
    if (entry.resolve) {
      entry.resolve();
    }

    // Abort any pending request that hasn't been granted yet
    try { entry.abortController.abort(); } catch { /* ignored */ }

    this.activeLocks.delete(lockName);
    return true;
  }

  /**
   * Check whether a named lock is currently held or pending.
   */
  isHeld(lockName: string): boolean {
    return this.activeLocks.has(lockName);
  }

  /**
   * Release all currently held locks. Called automatically on destroy.
   */
  releaseAll(): void {
    this.activeLocks.forEach((_entry, lockName) => {
      this.release(lockName);
    });
  }

  ngOnDestroy(): void {
    this.releaseAll();
  }
}
